/**
 * Where the Brave Things Thrive — Book 2 Table of Contents
 *
 * Access flow:
 *   1. Arrive with ?token=xyz from library
 *   2. Validate token + admin role
 *   3. Show the Book 2 TOC
 *
 * Chapter 1 is live (story content complete).
 * Chapters 2–6 are listed as "Coming Soon".
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ArrowLeft, Home, BookOpen, Shield, AlertTriangle, Lock, Ear, Mountain, Heart, Trophy, Eye, Sparkles, Map, Play, Leaf, GraduationCap,
  ChevronDown, ChevronUp, MessageCircle, Sun, BookOpenCheck, Clock, Bookmark, Users, Microscope
} from "lucide-react";
import { loadBookmark, hasBookmark, calculateProgress, formatTimestamp, getChapterTitle, saveReadingPosition, clearBookmark } from "@/lib/bookmark";
import { WTBTTForestJourneyMap } from "@/components/wtbtt-forest-journey-map";
import { startSession, trackButton, trackPage, sendAnalytics } from "@/lib/analytics/client";

interface TokenValidationResponse {
  valid: boolean;
  userId?: string;
  bookId?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  return_info?: {
    url: string;
    label: string;
    platform: string;
  };
}

// ── Chapter catalogue ─────────────────────────────────────────────────────────

const chapters = [
  {
    id: 7,
    title: "Listening with My Whole Body",
    theme: "Listening and Attention",
    description: "Mila the squirrel follows Grandpa Tortoise's note to listen to the wind's whispers. When the friends chatter and hum over the forest's sounds, they get lost — and discover that listening on purpose is the real magic.",
    icon: "👂",
    cueIcon: <Ear className="w-4 h-4" />,
    cueName: "Listening Ears",
    bgColor: "bg-sky-50",
    borderColor: "border-sky-200",
    badgeColor: "bg-sky-100 text-sky-700 border-sky-200",
    available: true,
  },
  {
    id: 8,
    title: "Waiting with a Plan",
    theme: "Patience and Waiting",
    description: "Max the mouse loses his favourite acorn to the river and is overwhelmed by big feelings. Grandpa Tortoise teaches him the Steady Rock cue — finding stillness and weight inside himself even when things go wrong.",
    icon: "🪨",
    cueIcon: <Mountain className="w-4 h-4" />,
    cueName: "Steady Rock",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    badgeColor: "bg-amber-100 text-amber-700 border-amber-200",
    available: true,
  },
  {
    id: 9,
    title: "The Oops Banner",
    theme: "Self-Compassion",
    description: "Mila the meerkat paints a big WELCOME banner for the festival — but a paint drip smudges the O. When her inner critic gets loud, Grandpa Tortoise and Bella remind her that the kindest voice she has is her own.",
    icon: "🐾",
    cueIcon: <Heart className="w-4 h-4" fill="currentColor" />,
    cueName: "Heart-in-Paw",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-200",
    badgeColor: "bg-rose-100 text-rose-700 border-rose-200",
    available: true,
  },
  {
    id: 10,
    title: "The Tower Trouble",
    theme: "Perseverance / Frustration Tolerance",
    description: "The festival's 'Big Build' contest is in trouble! Bella and the helpers encourage the younger animals to keep stacking — celebrating every tiny try, even the wobbly ones. Every attempt counts.",
    icon: "🏆",
    cueIcon: <Trophy className="w-4 h-4" />,
    cueName: "Try-Again Trophy",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    badgeColor: "bg-orange-100 text-orange-700 border-orange-200",
    available: true,
  },
  {
    id: 11,
    title: "Fixing Feelings and Friendship",
    theme: "Perspective-Taking / Empathy",
    description: "The festival's Mirror Maze is in trouble! Marnie the mole is hiding, and younger animals are lost inside. The friends discover that helping starts with seeing the world through someone else's eyes.",
    icon: "👁️",
    cueIcon: <Eye className="w-4 h-4" />,
    cueName: "Seeing Eyes",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    badgeColor: "bg-teal-100 text-teal-700 border-teal-200",
    available: true,
  },
  {
    id: 12,
    title: "The Festival Finale",
    theme: "Conflict Resolution & Repair",
    description: "Pip accidentally crashes Mila's berry tarts! Accusations fly and feelings run hot — until Grandpa Tortoise gathers everyone in a circle and shows them that what matters is how we fix things together.",
    icon: "🤝",
    cueIcon: <Sparkles className="w-4 h-4" />,
    cueName: "Repair Heart",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    badgeColor: "bg-purple-100 text-purple-700 border-purple-200",
    available: true,
  },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function WTBTTBook() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [tokenData, setTokenData] = useState<TokenValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);

  // "How to Read" / "Message to Readers" collapsible state — linked like Book 1
  const [isGuideExpanded, setIsGuideExpanded] = useState(false);
  const [isEditorNoteExpanded, setIsEditorNoteExpanded] = useState(false);

  // Bookmark / Continue Reading
  const [currentBookmark, setCurrentBookmark] = useState<ReturnType<typeof loadBookmark>>(null);

  // ── Analytics ─────────────────────────────────────────────────────────────
  // Track whether we've already started the analytics session so we don't
  // call startSession() twice if the component re-renders.
  const analyticsStarted = useRef(false);

  useEffect(() => { validateAccess(); }, []);

  // Start analytics session once access is confirmed
  useEffect(() => {
    if (isValidToken && !analyticsStarted.current) {
      analyticsStarted.current = true;
      (async () => {
        await startSession();
        // Track the TOC page itself (chapter 0 = "navigation" context)
        await trackPage(0, 0, "toc");
      })();
    }
  }, [isValidToken]);

  const validateAccess = async () => {
    try {
      if (!isAdmin) {
        setError("This book is currently available to administrators only.");
        setTimeout(() => navigate("/library"), 3000);
        return;
      }

      const token = searchParams.get("token");
      if (!token) {
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await fetch("/api/book-access/validate-enhanced", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, bookId: "wtbtt" }),
      });

      const data: TokenValidationResponse = await response.json();

      if (data.valid && data.user?.role === "admin") {
        setIsValidToken(true);
        setTokenData(data);
        // Load any saved reading position
        try {
          setCurrentBookmark(loadBookmark());
        } catch {
          // ignore bookmark errors
        }
      } else {
        setError("This book is currently available to administrators only.");
        setTimeout(() => navigate("/library"), 3000);
      }
    } catch {
      setError("Failed to validate access.");
      setTimeout(() => navigate("/library"), 3000);
    } finally {
      setIsValidating(false);
    }
  };

  const handleBackToLibrary = async () => {
    await trackButton("wtbtt_back_to_library_button", { navigation_source: "home_button" });
    await sendAnalytics(0, 0);
    if (tokenData?.return_info?.url) {
      window.location.href = tokenData.return_info.url;
    } else {
      navigate("/library");
    }
  };

  const handleContinueReading = (chapterId: number, spreadIndex: number) => {
    navigate(`/book/wtbtt/chapter/${chapterId}/story?spread=${spreadIndex - 1}`);
  };

  const handleChapterClick = async (chapter: typeof chapters[number]) => {
    if (chapter.available) {
      await trackButton(`wtbtt_chapter_${chapter.id}_navigation`, { navigation_source: "toc" });
      await sendAnalytics(0, 0);
      navigate(`/book/wtbtt/chapter/${chapter.id}/story`);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 mx-auto mb-6"
          >
            <Shield className="w-16 h-16 text-sky-600" />
          </motion.div>
          <h2 className="text-xl font-bold text-forest-900 mb-2">Validating Access</h2>
          <div className="flex items-center justify-center gap-2 text-forest-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Checking permissions…</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (!isValidToken || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 to-coral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-coral-200 bg-white/95">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-6 text-coral-500" />
            <h2 className="text-xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              {error ?? "You don't have permission to access this book."}
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={handleBackToLibrary} className="bg-forest-600 hover:bg-forest-700 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Library
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="border-forest-200 text-forest-600">
                <Home className="w-4 h-4 mr-2" /> Go to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Full-screen map overlay ──────────────────────────────────────────────────
  if (showMap) {
    const urlToken = searchParams.get("token");
    return (
      <WTBTTForestJourneyMap
        currentChapter={7}
        completedChapters={[]}
        token={urlToken}
        onClose={async () => {
          await trackButton("wtbtt_map_back_to_contents", { navigation_source: "home_button" });
          setShowMap(false);
        }}
      />
    );
  }

  // ── Success: Table of Contents ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-teal-50">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-sky-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-bold text-forest-800">Dira Stories</span>
            <span className="text-forest-400">•</span>
            <h1 className="text-xl font-bold text-forest-800">Book 2: Where the Brave Things Thrive</h1>
          </div>
          <div className="flex items-center gap-3">
            {tokenData?.user && (
              <Badge className="bg-sky-100 text-sky-700 border-sky-200">
                👋 {tokenData.user.name}
              </Badge>
            )}
            <Badge className="bg-red-500 text-white">
              <Shield className="w-3 h-3 mr-1" /> Admin Preview
            </Badge>
            <Button
              variant="outline"
              onClick={handleBackToLibrary}
              className="border-sky-300 text-sky-700 bg-sky-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {tokenData?.return_info?.label ?? "My Library"}
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <h2 className="text-5xl font-bold text-forest-800 mb-4">
            Where the Brave Things Thrive
          </h2>
          <p className="text-xl text-forest-600 mb-3">
            Book 2 — The Forest of Wonder continues
          </p>
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            🚧 Admin Preview — In Development
          </Badge>
        </motion.div>

        {/* TOC heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-8"
        >
          <h3 className="text-3xl font-bold text-forest-800 mb-2">📚 Table of Contents</h3>
          <p className="text-forest-600">
            Join Mila, Max, Bella, and Grandpa Tortoise on new adventures
          </p>
        </motion.div>

        {/* Continue Reading — only shown when bookmark exists */}
        {currentBookmark && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="flex justify-center mb-8"
          >
            <div className="w-full max-w-2xl">
              <Card
                className="bg-green-50 border-green-200 border-2 cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => handleContinueReading(currentBookmark.chapterId, currentBookmark.spreadIndex + 1)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-6 h-6 text-green-600" />
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-lg font-semibold text-green-800">Continue Reading</span>
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs px-2 py-1">
                            {calculateProgress(currentBookmark.chapterId, currentBookmark.spreadIndex)}% complete
                          </Badge>
                        </div>
                        <div className="text-green-700 font-medium">
                          Chapter {currentBookmark.chapterId}: {getChapterTitle(currentBookmark.chapterId)}, Page {currentBookmark.spreadIndex + 1}
                        </div>
                        <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatTimestamp(currentBookmark.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-300 text-green-700 bg-white hover:bg-green-50 ml-4 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearBookmark();
                        setCurrentBookmark(null);
                      }}
                    >
                      <Bookmark className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* How to Read + Message to Readers — linked collapsible pair (matches Book 1) */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* How to Read This Book */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="h-full bg-white/90 backdrop-blur-sm border-sky-200 border-2">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={async () => {
                  await trackButton("wtbtt_how_to_read_button", { navigation_source: "toc" });
                  setIsGuideExpanded(!isGuideExpanded);
                  setIsEditorNoteExpanded(!isGuideExpanded);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700">Guide</Badge>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">🌟</div>
                    {isGuideExpanded ? <ChevronUp className="w-5 h-5 text-sky-600" /> : <ChevronDown className="w-5 h-5 text-sky-600" />}
                  </div>
                </div>
                <CardTitle className="text-sky-900 text-lg">How to Read This Book</CardTitle>
              </CardHeader>
              <motion.div
                initial={false}
                animate={{ height: isGuideExpanded ? "auto" : 0, opacity: isGuideExpanded ? 1 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <CardContent className="text-center">
                  <div className="text-sky-700 mb-4 space-y-2">
                    <p className="text-base font-medium">Look for the Magic Cue Page!</p>
                    <p className="text-sm leading-relaxed">When you see the cue page, pause and do the activity together.</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[["👂","Listen"],["🪨","Steady"],["🎨","Kind"],["🏆","Try"],["👁️","See"],["🤝","Repair"]].map(([e, l]) => (
                      <div key={l} className="text-center p-2 bg-sky-50 rounded border border-sky-200">
                        <div className="text-lg mb-1">{e}</div>
                        <div className="text-xs text-sky-700">{l}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                <div className="px-6 pb-6">
                  <h4 className="text-lg font-semibold text-sky-900 mb-3">How to Use This Book</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <BookOpenCheck className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="text-left">
                        <h5 className="font-semibold text-sky-900 text-sm">Read Together:</h5>
                        <p className="text-xs text-sky-700 mt-1">Take turns reading or listen as someone reads aloud. A glossary is available below.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="text-left">
                        <h5 className="font-semibold text-sky-900 text-sm">Pause for Cues:</h5>
                        <p className="text-xs text-sky-700 mt-1">When you see activity symbols, stop and do the suggested activities.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <h5 className="font-semibold text-sky-900 text-sm">Talk About It:</h5>
                        <p className="text-xs text-sky-700 mt-1">Share thoughts, feelings, and connections to your own life.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Sun className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div className="text-left">
                        <h5 className="font-semibold text-sky-900 text-sm">Take Your Time:</h5>
                        <p className="text-xs text-sky-700 mt-1">There's no rush — enjoy the journey through the Forest of Wonder!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Card>
          </motion.div>

          {/* A Message to Our Readers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <Card className="h-full bg-white/90 backdrop-blur-sm border-sky-200 border-2">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={async () => {
                  await trackButton("wtbtt_message_to_readers_button", { navigation_source: "toc" });
                  setIsEditorNoteExpanded(!isEditorNoteExpanded);
                  setIsGuideExpanded(!isEditorNoteExpanded);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700">Editor's Note</Badge>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">💌</div>
                    {isEditorNoteExpanded ? <ChevronUp className="w-5 h-5 text-sky-600" /> : <ChevronDown className="w-5 h-5 text-sky-600" />}
                  </div>
                </div>
                <CardTitle className="text-sky-900 text-lg">A Message to Our Readers</CardTitle>
              </CardHeader>
              <motion.div
                initial={false}
                animate={{ height: isEditorNoteExpanded ? "auto" : 0, opacity: isEditorNoteExpanded ? 1 : 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <CardContent>
                  <div className="text-sky-700 text-base leading-relaxed space-y-3">
                    <p>
                      Thank you so much for being among the first readers of "Where the Brave Things Thrive." This is Book 2 of the Brave Things series — continuing the adventures of Mila, Max, Bella, and Grandpa Tortoise in the Forest of Wonder.
                    </p>
                    <p>
                      Each chapter explores a social-emotional skill that children need to thrive — listening, patience, self-compassion, perseverance, empathy, and repair. These are the brave things that grow quietly inside us, one small step at a time.
                    </p>
                    <p>
                      We hope reading this book together sparks rich conversations and gives children language and tools to navigate the complex inner world of feelings.
                    </p>
                    <p className="font-medium">
                      With gratitude,<br />
                      The Brave Things Books Team
                    </p>
                    <p className="text-center pt-2 font-bold text-sm">
                      Click below to share your feedback!
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                      <a
                        href="https://forms.clickup.com/2370929/f/28bbh-75171/DGU9D6EC4XJIQE26DB"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-700 underline font-medium text-sm"
                      >
                        Parent/Caregiver
                      </a>
                      <a
                        href="https://forms.clickup.com/2370929/f/28bbh-75151/8M474434L1ITR53Z4U"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-700 underline font-medium text-sm"
                      >
                        Educator
                      </a>
                    </div>
                  </div>
                </CardContent>
              </motion.div>
            </Card>
          </motion.div>
        </div>

        {/* 🗺️ Forest Journey Map Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="flex justify-center mb-8"
        >
          <div className="w-full max-w-2xl">
            <div
              className="rounded-2xl border-2 border-sky-200 bg-gradient-to-r from-sky-50 via-teal-50 to-blue-50
                         shadow-md cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              onClick={async () => {
                await trackButton("wtbtt_journey_map_button", { navigation_source: "toc" });
                setShowMap(true);
              }}
            >
              <div className="flex items-center gap-5 p-6">
                {/* Icon cluster */}
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-sky-100 border-2 border-sky-200
                                flex items-center justify-center text-3xl shadow-sm">
                  🗺️
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Map className="w-4 h-4 text-sky-600" />
                    <span className="text-xs font-semibold text-sky-600 uppercase tracking-wide">
                      Forest of Wonder
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-sky-900 mb-1">
                    Explore the Journey Map
                  </h3>
                  <p className="text-sm text-sky-700 leading-relaxed">
                    See all six chapters on an interactive map and jump to any adventure with animated paw-print trails.
                  </p>
                </div>

                {/* Chapter emojis preview */}
                <div className="hidden sm:flex flex-col gap-1">
                  <div className="flex gap-1">
                    {["👂","🪨","🎨"].map((e) => (
                      <span key={e} className="text-xl w-8 h-8 rounded-full bg-white/70 flex items-center justify-center shadow-sm">{e}</span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    {["🏗️","🪟","🎪"].map((e) => (
                      <span key={e} className="text-xl w-8 h-8 rounded-full bg-white/70 flex items-center justify-center shadow-sm">{e}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Colour bar at bottom */}
              <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-teal-400 to-blue-400" />
            </div>
          </div>
        </motion.div>

        {/* Story Introduction Card (centered, before chapters) */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="flex justify-center mb-8"
        >
          <div className="w-full max-w-md">
            <Card
              className="h-full bg-white/90 backdrop-blur-sm border-sky-200 border-2 shadow-lg cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              onClick={async () => {
                await trackButton("wtbtt_story_introduction_button", { navigation_source: "toc" });
                await sendAnalytics(0, 0);
                navigate("/book/wtbtt/introduction");
              }}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700">
                    Start Here
                  </Badge>
                  <div className="text-2xl">🌟</div>
                </div>
                <CardTitle className="text-sky-900 text-lg text-center">
                  Story Introduction
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-sky-700 mb-4 space-y-2">
                  <p className="text-sm font-medium">Meet the Characters!</p>
                  <p className="text-xs leading-relaxed">
                    Rejoin Mila, Max, Bella, and Grandpa Tortoise as they prepare for the great Festival of Wonder.
                  </p>
                </div>
                <Button
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await trackButton("wtbtt_story_introduction_button", { navigation_source: "toc" });
                    await sendAnalytics(0, 0);
                    navigate("/book/wtbtt/introduction");
                  }}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Here
                </Button>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Chapter cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {chapters.map((chapter, index) => (
            <motion.div
              key={chapter.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
            >
              <Card
                className={`h-full ${chapter.bgColor} ${chapter.borderColor} border-2 shadow-md transition-all duration-300 group ${
                  chapter.available ? "cursor-pointer hover:shadow-xl hover:-translate-y-1" : "opacity-60 cursor-default"
                }`}
                onClick={() => handleChapterClick(chapter)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs">Chapter {chapter.id}</Badge>
                    <div className="text-3xl">{chapter.icon}</div>
                  </div>
                  <CardTitle className="text-base text-forest-800">{chapter.title}</CardTitle>
                  {chapter.theme && (
                    <p className="text-xs font-medium text-forest-600 uppercase tracking-wide">{chapter.theme}</p>
                  )}
                </CardHeader>

                <CardContent className="pt-0">
                  <p className="text-sm text-forest-700 mb-4 leading-relaxed">{chapter.description}</p>

                  {chapter.available ? (
                    <div className="space-y-2">
                      {chapter.cueName && (
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${chapter.badgeColor}`}>
                          {chapter.cueIcon ?? <Ear className="w-3 h-3" />} {chapter.cueName}
                        </div>
                      )}
                      <div className="space-y-3">
                        {/* Row 1: chapter number of pages + Read Story button (matches Book 1 layout) */}
                        <div className="flex items-center justify-between text-sm text-forest-600">
                          <span className="text-xs text-forest-500">11 pages</span>
                          <Button
                            size="sm"
                            variant="default"
                            className={`h-8 px-3 text-xs text-white ${chapter.id === 7 ? "bg-sky-500 hover:bg-sky-600" : chapter.id === 8 ? "bg-amber-500 hover:bg-amber-600" : chapter.id === 9 ? "bg-rose-500 hover:bg-rose-600" : chapter.id === 10 ? "bg-orange-500 hover:bg-orange-600" : chapter.id === 11 ? "bg-teal-500 hover:bg-teal-600" : chapter.id === 12 ? "bg-purple-500 hover:bg-purple-600" : "bg-sky-500 hover:bg-sky-600"}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await trackButton(`wtbtt_chapter_${chapter.id}_navigation`, { navigation_source: "toc" });
                              await sendAnalytics(0, 0);
                              navigate(`/book/wtbtt/chapter/${chapter.id}/story`);
                            }}
                          >
                            <BookOpen className="w-3 h-3 mr-1" />
                            Read Story
                          </Button>
                        </div>
                        {/* Row 2: The Science + Family Guide side by side */}
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className={`w-[105px] px-2 py-1 h-7 text-[10px] bg-white/50 ${chapter.id === 7 ? "border-sky-300 text-sky-700 hover:bg-sky-50" : chapter.id === 8 ? "border-amber-300 text-amber-700 hover:bg-amber-50" : chapter.id === 9 ? "border-rose-300 text-rose-700 hover:bg-rose-50" : chapter.id === 10 ? "border-orange-300 text-orange-700 hover:bg-orange-50" : chapter.id === 11 ? "border-teal-300 text-teal-700 hover:bg-teal-50" : chapter.id === 12 ? "border-purple-300 text-purple-700 hover:bg-purple-50" : "border-sky-300 text-sky-700"}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await trackButton(`wtbtt_chapter_${chapter.id}_activity`, { navigation_source: "toc" });
                              await sendAnalytics(0, 0);
                              navigate(`/book/wtbtt/chapter/${chapter.id}/activity`);
                            }}
                          >
                            <Microscope className="w-2.5 h-2.5 mr-0.5" />
                            The Science
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`w-[105px] px-2 py-1 h-7 text-[10px] bg-white/50 ${chapter.id === 7 ? "border-sky-300 text-sky-700 hover:bg-sky-50" : chapter.id === 8 ? "border-amber-300 text-amber-700 hover:bg-amber-50" : chapter.id === 9 ? "border-rose-300 text-rose-700 hover:bg-rose-50" : chapter.id === 10 ? "border-orange-300 text-orange-700 hover:bg-orange-50" : chapter.id === 11 ? "border-teal-300 text-teal-700 hover:bg-teal-50" : chapter.id === 12 ? "border-purple-300 text-purple-700 hover:bg-purple-50" : "border-sky-300 text-sky-700"}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await trackButton(`wtbtt_chapter_${chapter.id}_family_guide`, { navigation_source: "toc" });
                              await sendAnalytics(0, 0);
                              navigate(`/book/wtbtt/chapter/${chapter.id}/activity#family-discussion`);
                            }}
                          >
                            <Users className="w-2.5 h-2.5 mr-0.5" />
                            Family Guide
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-3 bg-gray-100 border border-gray-200 rounded-lg">
                      <Lock className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                      <p className="text-xs font-medium text-gray-500">Coming Soon</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Glossary Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="flex justify-center mb-8"
        >
          <div className="w-full max-w-2xl">
            <Card
              className="h-full bg-white/90 backdrop-blur-sm border-sky-200 border-2 shadow-lg cursor-pointer hover:shadow-xl hover:border-sky-300 transition-all duration-300"
              onClick={async () => {
                await trackButton("wtbtt_glossary_button", { navigation_source: "toc" });
                await sendAnalytics(0, 0);
                navigate("/book/glossary");
              }}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700">
                    Word List
                  </Badge>
                  <div className="text-2xl">📖</div>
                </div>
                <CardTitle className="text-sky-900 text-lg text-center">
                  Glossary
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-sky-700 mb-4 space-y-2">
                  <p className="text-sm font-medium">Emotional Words &amp; Nature Words</p>
                  <p className="text-xs leading-relaxed">
                    Explore meanings of emotional vocabulary and nature words from the story with simple, child-friendly definitions.
                  </p>
                </div>
                <div className="flex justify-center gap-3 mb-4">
                  <div className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                    <Heart className="w-3 h-3" />
                    <span>Emotions</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-3 py-1 rounded-full border border-green-200">
                    <Leaf className="w-3 h-3" />
                    <span>Nature</span>
                  </div>
                </div>
                <Button
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await trackButton("wtbtt_glossary_button", { navigation_source: "toc" });
                    await sendAnalytics(0, 0);
                    navigate("/book/glossary");
                  }}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Explore Words
                </Button>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Selected Research Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="flex justify-center mb-10"
        >
          <div className="w-full max-w-2xl">
            <Card
              className="h-full bg-white/90 backdrop-blur-sm border-sky-200 border-2 shadow-lg cursor-pointer hover:shadow-xl hover:border-sky-300 transition-all duration-300"
              onClick={async () => {
                await trackButton("wtbtt_research_button", { navigation_source: "toc" });
                await sendAnalytics(0, 0);
                navigate("/book/wtbtt/references");
              }}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700">
                    Research
                  </Badge>
                  <div className="text-2xl">📚</div>
                </div>
                <CardTitle className="text-sky-900 text-lg text-center">
                  Selected Research Behind the Stories and Activities
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-sky-700 mb-4 space-y-2">
                  <p className="text-sm font-medium">Academic Foundations</p>
                  <p className="text-xs leading-relaxed">
                    Explore the research and evidence-based practices that inform each chapter's social-emotional learning concepts.
                  </p>
                </div>
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {[
                    { ch: 7, bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
                    { ch: 8, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
                    { ch: 9, bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700" },
                    { ch: 10, bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700" },
                    { ch: 11, bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700" },
                    { ch: 12, bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700" },
                  ].map(({ ch, bg, border, text }) => (
                    <div
                      key={ch}
                      className={`text-center p-2 rounded border cursor-pointer transition-colors ${bg} ${border}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await trackButton(`wtbtt_research_chapter_${ch}_button`, { navigation_source: "toc" });
                        await sendAnalytics(0, 0);
                        navigate(`/book/wtbtt/references#chapter-${ch}`);
                      }}
                    >
                      <div className={`text-xs font-medium ${text}`}>Ch {ch}</div>
                    </div>
                  ))}
                </div>
                <Button
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await trackButton("wtbtt_research_button", { navigation_source: "toc" });
                    await sendAnalytics(0, 0);
                    navigate("/book/wtbtt/references");
                  }}
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  View Research
                </Button>
              </CardContent>
            </Card>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
